import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import {
    ChangePasswordBindingModel,
    ProfileBindingModel
} from "@switcherstudio/switcher-api-client";
import rollbar from "helpers/rollbar";
import { useCallback, useMemo, useState } from "react";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store/store";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { refreshToken, getUserInfo } from "store/user/thunks";
import { EditProfileForm } from "./EditProfileForm";
import { ChangePasswordForm } from "./ChangePasswordForm";
import { LoadingOverlay } from "components/loading-overlay";
import { useNavigate } from "react-router-dom";

export const ProfilePage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const [isReloading, setIsReloading] = useState(false);

    const {
        dispatchApiRequest: getProfile,
        data: profileData,
        loading: profileDataLoading
    } = useSwitcherClient((client) => client.account_GetProfile, {
        requestImmediately: true,
        onError(e) {
            rollbar.error("Error getting account profile", e);

            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: e.message
                })
            );
        },
        onSuccess: () => {
            setIsReloading(false);
        }
    });

    const { dispatchApiRequest: updateProfile, loading: updateProfileLoading } =
        useSwitcherClient((client) => client.account_PutProfile, {
            requestImmediately: false,
            onSuccess: () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Success,
                        message: "profile:success-message-profile"
                    })
                );
            },
            onError(e, response) {
                rollbar.error("Error updating user profile", e, response);

                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: e.message
                    })
                );
            }
        });

    const {
        dispatchApiRequest: changePassword,
        loading: changePasswordLoading
    } = useSwitcherClient((client) => client.account_ChangePassword, {
        requestImmediately: false,
        onSuccess: () => {
            dispatch(
                addNotification({
                    type: NotificationType.Success,
                    message: "messages:password-updated"
                })
            );
        },
        onError(e, response) {
            rollbar.error("Error changing password", e, response);

            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: e.message
                })
            );
        }
    });

    const onChangePasswordSubmit = useCallback(
        async (form: ChangePasswordBindingModel) => {
            await changePassword([
                {
                    OldPassword: form.OldPassword,
                    NewPassword: form.NewPassword,
                    ConfirmPassword: form.ConfirmPassword
                }
            ]);
        },
        [changePassword]
    );

    const onEditProfileSubmit = useCallback(
        async (form: ProfileBindingModel) => {
            setIsReloading(true);
            await updateProfile([form]);
            await dispatch(refreshToken());
            await dispatch(getUserInfo());
            await getProfile();
        },
        [dispatch, updateProfile, getProfile]
    );

    const isLoading = useMemo(() => {
        return (
            profileDataLoading ||
            updateProfileLoading ||
            changePasswordLoading ||
            isReloading
        );
    }, [
        profileDataLoading,
        updateProfileLoading,
        changePasswordLoading,
        isReloading
    ]);

    const navigate = useNavigate();

    return (
        <div className={styles["profile-form-container"]}>
            <LoadingOverlay isLoading={isLoading}>
                {profileData && (
                    <>
                        <EditProfileForm
                            onSubmit={onEditProfileSubmit}
                            profile={profileData}
                            loading={profileDataLoading || updateProfileLoading}
                        />

                        <hr />

                        <ChangePasswordForm
                            onSubmit={onChangePasswordSubmit}
                            loading={changePasswordLoading}
                        />
                        <hr />

                        <div className="row">
                            <div className="col-8">
                                <button
                                    className={styles["delete-account-link"]}
                                    onClick={() => {
                                        navigate("/account-deletion");
                                    }}
                                    // onClick={() => setModalIsOpen(true)} // TODO: Implement modal for account deletion in future story
                                >
                                    {t(
                                        "delete-account:delete-account"
                                    ).toUpperCase()}
                                </button>
                                <p
                                    className={
                                        styles["delete-account-description"]
                                    }
                                >
                                    {t("profile:delete-account-description")}
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </LoadingOverlay>
        </div>
    );
};
